if (!customElements.get('product-bundle-offer')) {
  customElements.define(
    'product-bundle-offer',
    class ProductBundleOffer extends HTMLElement {
      constructor() {
        super();
        this.timerId = null;
        this.busy = false;
        this.viewModal = null;
        this.historyLocked = false;
        this.closingViaUi = false;
        this.onCtaClick = this.onCtaClick.bind(this);
        this.onCartSync = this.onCartSync.bind(this);
        this.onPopState = this.onPopState.bind(this);
        this.onModalClosed = this.onModalClosed.bind(this);
      }

      connectedCallback() {
        this.cta = this.querySelector('[data-pbo-atc]');
        this.ctaText = this.cta?.querySelector('.pbo-cta__text');
        this.cta?.addEventListener('click', this.onCtaClick, { passive: false });

        if (this.dataset.viewModal) {
          this.viewModal = document.querySelector(this.dataset.viewModal);
        }

        if (this.dataset.showTimer === 'true') {
          this.initTimer();
        }

        this.clearLegacyLockStorage();
        this.syncCtaWithCart();

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartSync);
        document.addEventListener('visibilitychange', this.onCartSync);
        window.addEventListener('pageshow', this.onCartSync);
        window.addEventListener('popstate', this.onPopState);
        document.body.addEventListener('modalClosed', this.onModalClosed);

        if (this.viewModal) {
          this.modalObserver = new MutationObserver(() => {
            if (this.viewModal.hasAttribute('open')) {
              this.lockHistoryOnOpen();
            }
          });
          this.modalObserver.observe(this.viewModal, { attributes: true, attributeFilter: ['open'] });
        }
      }

      disconnectedCallback() {
        this.cta?.removeEventListener('click', this.onCtaClick);
        document.removeEventListener('visibilitychange', this.onCartSync);
        window.removeEventListener('pageshow', this.onCartSync);
        window.removeEventListener('popstate', this.onPopState);
        document.body.removeEventListener('modalClosed', this.onModalClosed);
        this.modalObserver?.disconnect();
        if (this.timerId) clearInterval(this.timerId);
        this.cartUpdateUnsubscriber?.();
      }

      lockHistoryOnOpen() {
        if (!this.viewModal?.hasAttribute('open') || this.historyLocked) return;
        history.pushState({ pboModal: this.viewModal.id }, '');
        this.historyLocked = true;
      }

      onPopState() {
        if (this.closingViaUi) {
          this.closingViaUi = false;
          return;
        }
        if (this.viewModal?.hasAttribute('open')) {
          this.historyLocked = false;
          this.viewModal.hide();
        }
      }

      onModalClosed() {
        if (!this.viewModal || this.viewModal.hasAttribute('open')) return;
        if (!this.historyLocked) return;
        this.historyLocked = false;
        this.closingViaUi = true;
        history.back();
      }

      onCartSync(event) {
        if (event?.type === 'visibilitychange' && document.visibilityState !== 'visible') return;
        const cartData = event?.cartData;
        this.syncCtaWithCart(cartData);
      }

      storageKey() {
        return `pbo_bundle_locked_${this.dataset.sectionId}`;
      }

      clearLegacyLockStorage() {
        try {
          sessionStorage.removeItem(this.storageKey());
        } catch (e) {
          /* ignore */
        }
      }

      getVariantIds() {
        // A configured kit variant always wins. Never add the visual
        // component products when the bundle has its own sellable variant.
        if (this.dataset.cartVariantId) {
          return [this.dataset.cartVariantId];
        }
        return Array.from(this.querySelectorAll('[data-pbo-variant-id]'))
          .map((el) => el.dataset.pboVariantId)
          .filter(Boolean);
      }

      setCtaLabel(text) {
        if (this.ctaText) this.ctaText.textContent = text;
        else if (this.cta) this.cta.textContent = text;
      }

      isLocked() {
        return this.cta?.classList.contains('is-locked');
      }

      lockSuccess(options = {}) {
        if (!this.cta) return;
        const { animate = true } = options;
        const successLabel = this.cta.dataset.successLabel || 'Added to cart!';
        this.setCtaLabel(successLabel);
        this.cta.classList.remove('is-loading', 'is-animating');
        this.cta.classList.add('is-success', 'is-locked');
        this.cta.disabled = true;
        this.cta.setAttribute('aria-disabled', 'true');
        if (animate) {
          this.cta.classList.add('is-success-pop');
          setTimeout(() => this.cta?.classList.remove('is-success-pop'), 400);
        }
        this.busy = false;
      }

      unlockCta() {
        if (!this.cta || this.busy) return;
        this.cta.classList.remove('is-success', 'is-locked', 'is-loading', 'is-animating', 'is-success-pop');
        this.cta.disabled = false;
        this.cta.removeAttribute('aria-disabled');
        this.setCtaLabel(this.cta.dataset.defaultLabel || 'Add bundle to cart');
      }

      syncCtaWithCart(cartPayload) {
        if (!this.cta) return;
        const ids = this.getVariantIds();
        if (!ids.length) return;

        const apply = (cart) => {
          const items = cart?.items || [];
          const qtyByVariant = new Map();
          items.forEach((item) => {
            const id = String(item.variant_id || item.id);
            qtyByVariant.set(id, (qtyByVariant.get(id) || 0) + (item.quantity || 0));
          });
          const allInCart = ids.every((id) => (qtyByVariant.get(String(id)) || 0) > 0);
          if (allInCart) {
            this.lockSuccess({ animate: false });
          } else {
            this.unlockCta();
          }
        };

        if (
          cartPayload &&
          Array.isArray(cartPayload.items) &&
          typeof cartPayload.item_count === 'number'
        ) {
          apply(cartPayload);
          return;
        }

        fetch(`${routes.cart_url}.js`)
          .then((r) => r.json())
          .then(apply)
          .catch(() => {});
      }

      initTimer() {
        const mode = this.dataset.timerMode === 'days' ? 'days' : 'hours';
        const daysEl = this.querySelector('[data-pbo-d]');
        const hoursEl = this.querySelector('[data-pbo-h]');
        const minsEl = this.querySelector('[data-pbo-m]');
        const secsEl = this.querySelector('[data-pbo-s]');
        if (!hoursEl || !minsEl) return;

        let totalSeconds = parseInt(this.dataset.timerSeconds, 10) || 0;
        const persist = this.dataset.persistTimer === 'true';
        const storageKey = `pboTimer_v3_${this.dataset.sectionId}_${totalSeconds}_${mode}`;

        try {
          sessionStorage.removeItem(`pboTimer_${this.dataset.sectionId}`);
          sessionStorage.removeItem(`pboTimer_v2_${this.dataset.sectionId}`);
        } catch (e) {
          /* ignore */
        }

        if (persist && totalSeconds > 0) {
          try {
            const raw = sessionStorage.getItem(storageKey);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && typeof parsed.end === 'number' && parsed.end > Date.now()) {
                const left = Math.ceil((parsed.end - Date.now()) / 1000);
                if (left > 0 && left <= totalSeconds + 5) {
                  totalSeconds = left;
                }
              } else if (parsed && typeof parsed.end === 'number') {
                totalSeconds = 0;
              }
            }
          } catch (e) {
            /* ignore */
          }
        }

        if (totalSeconds > 0 && persist) {
          try {
            sessionStorage.setItem(
              storageKey,
              JSON.stringify({ end: Date.now() + totalSeconds * 1000 })
            );
          } catch (e) {
            /* ignore */
          }
        }

        const pad = (n) => String(Math.max(0, Math.floor(n))).padStart(2, '0');

        const render = () => {
          const safe = Math.max(0, totalSeconds);
          if (mode === 'days') {
            const days = Math.floor(safe / 86400);
            const hours = Math.floor((safe % 86400) / 3600);
            const mins = Math.floor((safe % 3600) / 60);
            if (daysEl) daysEl.textContent = pad(days);
            hoursEl.textContent = pad(hours);
            minsEl.textContent = pad(mins);
          } else {
            const hours = Math.floor(safe / 3600);
            const mins = Math.floor((safe % 3600) / 60);
            const secs = safe % 60;
            hoursEl.textContent = pad(hours);
            minsEl.textContent = pad(mins);
            if (secsEl) secsEl.textContent = pad(secs);
          }
        };

        render();
        if (totalSeconds <= 0) return;

        this.timerId = setInterval(() => {
          totalSeconds -= 1;
          if (totalSeconds <= 0) {
            totalSeconds = 0;
            render();
            clearInterval(this.timerId);
            this.timerId = null;
            return;
          }
          render();
        }, 1000);
      }

      onCtaClick(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.cta || this.busy || this.isLocked()) return;
        if (this.cta.classList.contains('is-loading')) return;

        const ids = this.getVariantIds();
        if (!ids.length) return;

        this.busy = true;
        this.cta.classList.add('is-animating');
        requestAnimationFrame(() => {
          this.cta.classList.remove('is-animating');
          this.cta.classList.add('is-loading');
          this.setCtaLabel(this.cta.dataset.loadingLabel || 'Adding...');
        });

        const items = ids.map((id) => ({
          id: parseInt(id, 10),
          quantity: 1,
          properties: {
            _add_source: 'Bundle offer',
          },
        }));

        fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.busy = false;
              this.cta.classList.remove('is-loading');
              this.setCtaLabel(this.cta.dataset.defaultLabel || 'Add bundle to cart');
              this.announce(response.description || 'Could not add to cart');
              return;
            }

            this.lockSuccess({ animate: true });
            this.refreshCartSections();

            fetch(`${routes.cart_url}.js`)
              .then((r) => r.json())
              .then((cart) => {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-bundle-offer',
                  cartData: cart,
                });
              })
              .catch(() => {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle-offer' });
              });

            this.announce(this.cta.dataset.successLabel || 'Added to cart!');
          })
          .catch((e) => {
            console.error(e);
            this.busy = false;
            this.cta.classList.remove('is-loading');
            this.setCtaLabel(this.cta.dataset.defaultLabel || 'Add bundle to cart');
          });
      }

      refreshCartSections() {
        const sectionIds = this.getSectionsToFetch().join(',');
        const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionIds)}`;

        fetch(url)
          .then((r) => r.json())
          .then((sections) => this.applySections(sections))
          .catch(() => {});
      }

      getSectionsToFetch() {
        return ['cart-icon-bubble', 'cart-drawer'];
      }

      applySections(sections) {
        if (!sections) return;

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer?.getSectionsToRender) {
          cartDrawer.getSectionsToRender().forEach((section) => {
            const sectionHtml = sections[section.id];
            if (!sectionHtml) return;
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!sectionElement) return;
            sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(sectionHtml, section.selector);
          });
          cartDrawer.classList.remove('is-empty');
          const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
          overlay?.addEventListener('click', cartDrawer.close.bind(cartDrawer));
        }

        const bubbleSection = document.getElementById('shopify-section-cart-icon-bubble');
        if (bubbleSection && sections['cart-icon-bubble']) {
          const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          const inner = html.querySelector('.shopify-section');
          if (inner) bubbleSection.innerHTML = inner.innerHTML;
        }

        cartDrawer?.setHeaderCartIconAccessibility?.();

        const countBadge = document.querySelector('#cart-icon-bubble .cart-count-bubble');
        if (countBadge) {
          countBadge.classList.add('pbr-count-pop');
          setTimeout(() => countBadge.classList.remove('pbr-count-pop'), 450);
        }
      }

      announce(message) {
        const live = this.querySelector('[data-pbo-live]');
        if (live) live.textContent = message;
      }
    }
  );
}
